import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatGBP, formatUKDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Shield, Pause, Play } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Admin() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ plan: "pro", status: "active", period_days: 30 });

  const load = async () => {
    setLoading(true);
    try {
      const [ov, bs] = await Promise.all([api.get("/admin/overview"), api.get("/admin/businesses")]);
      setOverview(ov.data); setBusinesses(bs.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user?.is_platform_admin) load(); }, [user]);

  if (!user) return null;
  if (!user.is_platform_admin) return <Navigate to="/app/dashboard" replace />;

  const toggleSuspend = async (b) => {
    await api.patch(`/admin/businesses/${b.id}`, { suspended: !b.suspended });
    toast.success(!b.suspended ? "Suspended" : "Reactivated");
    load();
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ plan: b.subscription?.plan || "pro", status: b.subscription?.status || "active", period_days: 30 });
  };

  const saveSub = async (e) => {
    e.preventDefault();
    await api.patch(`/admin/businesses/${editing.id}`, form);
    toast.success("Subscription updated");
    setEditing(null);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-page">
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6 text-secondary" />
        <h1 className="font-display text-3xl tracking-tight font-bold">Platform Admin</h1>
      </div>

      {overview && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <OvCell label="Businesses" value={overview.businesses} />
          <OvCell label="Active" value={overview.active} />
          <OvCell label="Trialing" value={overview.trialing} />
          <OvCell label="Suspended" value={overview.suspended} />
          <OvCell label="Revenue" value={formatGBP(overview.total_revenue)} />
        </div>
      )}

      <div className="mt-8 rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-left text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Business</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Users</th>
              <th className="px-4 py-3">Invoices</th>
              <th className="px-4 py-3">Revenue</th>
              <th className="px-4 py-3">Renews</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr> :
             businesses.map((b) => (
              <tr key={b.id} data-testid={`admin-business-${b.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{b.id.slice(0, 8)}</div>
                </td>
                <td className="px-4 py-3 capitalize">{b.subscription?.plan}</td>
                <td className="px-4 py-3"><StatusBadge status={b.suspended ? "suspended" : (b.subscription?.status || "")} /></td>
                <td className="px-4 py-3">{b.user_count}</td>
                <td className="px-4 py-3">{b.invoice_count}</td>
                <td className="px-4 py-3 font-medium">{formatGBP(b.paid_total)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{b.subscription?.current_period_end ? formatUKDate(b.subscription.current_period_end) : "—"}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <button onClick={() => openEdit(b)} className="text-xs rounded-md border border-border px-2 py-1 hover:bg-muted" data-testid={`admin-edit-${b.id}`}>Edit plan</button>
                  <button onClick={() => toggleSuspend(b)} className={`inline-flex items-center gap-1 text-xs rounded-md px-2 py-1 ${b.suspended ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`} data-testid={`admin-toggle-${b.id}`}>
                    {b.suspended ? <><Play className="h-3 w-3" /> Activate</> : <><Pause className="h-3 w-3" /> Suspend</>}
                  </button>
                </td>
              </tr>
             ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Adjust subscription</DialogTitle></DialogHeader>
          <form onSubmit={saveSub} className="space-y-3" data-testid="admin-edit-form">
            <div>
              <label className="text-sm font-medium">Plan</label>
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="admin-plan">
                <option value="trial">Trial</option>
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
                <option value="team">Team</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="admin-status">
                <option value="active">Active</option>
                <option value="trialing">Trialing</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Extend by (days)</label>
              <input type="number" value={form.period_days} onChange={(e) => setForm({ ...form, period_days: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="admin-period" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
              <button type="submit" className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="admin-save">Save</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OvCell({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-3xl font-bold">{value}</div>
    </div>
  );
}
